import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import {
  getGameResults,
  getGameResultDetails,
} from "../../services/gameResultService";
import { getMatches } from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";

const toastCfg = { autoClose: 1000 };

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Styles ─── */
const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 8 },
  addBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#0f172a", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 2px 6px rgba(15,23,42,0.2)" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 220, fontFamily: "inherit" },
  clearBtn: { position: "absolute", right: 8, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex", alignItems: "center" },
  select: { padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.81rem", color: "#475569", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 120 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "12px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" },
  pgBtnActive: { background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", border: "none", fontWeight: 700 },
};

const ms = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub: { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
};

const ts = {
  viewBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", background: "#fdf4ff", color: "#c026d3", border: "1px solid #fbcfe8", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600 },
};

/* ─── View Game Result Modal ─── */
const ViewGameModal = ({ show, onHide, resultData, matches }) => {
  if (!resultData) return null;
  const info = resultData.game_info || {};
  const stats = resultData.pool_stats || {};
  const settl = resultData.settlement_info || {};

  const match = matches.find((m) => String(m.id) === String(resultData.match_id));
  const matchName = match ? `${match.team_a} vs ${match.team_b}` : `Match ${resultData.match_id}`;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>{info.title || "Game Details"}</div>
          <div style={ms.headerSub}>{matchName}</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={{ padding: "24px", background: "#f8fafc", maxHeight: "75vh", overflowY: "auto" }}>
        
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: 24 }}>
          {/* Declaration Metadata */}
          <div style={{ flex: "1 1 300px", background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
             <h6 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>Settlement Log</h6>
             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
               <span style={{ color: "#64748b", fontWeight: 600 }}>Declared By:</span>
               <span style={{ color: "#1e293b", fontWeight: 800 }}>{settl.declared_by || "—"}</span>
             </div>
             <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
               <span style={{ color: "#64748b", fontWeight: 600 }}>Time:</span>
               <span style={{ color: "#1e293b", fontWeight: 800 }}>{settl.declared_at || "—"}</span>
             </div>
          </div>

          {/* Pool Stats Card */}
          <div style={{ flex: "1 1 300px", background: "#fff", borderRadius: 12, padding: 20, borderTop: "4px solid #3b82f6", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h6 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>Pool Ledger</h6>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Total Collection:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{stats.total_collection}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Total Bets Placed:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>{stats.total_bets_placed}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Admin Cut:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{stats.admin_cut_amount}</span>
            </div>
            <div style={{ height: 1, background: "#f1f5f9", margin: "12px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
               <span style={{ color: "#0f172a", fontWeight: 700 }}>Net Pool:</span>
               <span style={{ color: "#16a34a", fontWeight: 900 }}>₹{stats.net_distributable_pool}</span>
            </div>
          </div>
        </div>

      </div>
    </Modal>
  );
};


export const GameResultListLayer = () => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [matches, setMatches] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [leagueFilter, setLeagueFilter] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewData, setViewData] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, matchRes, leagueRes] = await Promise.all([
        getGameResults(), getMatches(), getLeagues()
      ]);
      if (res?.status) setData(Array.isArray(res.data) ? res.data : []);
      if (matchRes?.status) setMatches(matchRes.data || []);
      if (leagueRes?.status) setLeagues(leagueRes.data || []);
    } catch {
      toast.error("Failed to load records", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);


  const getMatchName = useCallback((match_id) => {
    const match = matches.find((m) => String(m.id) === String(match_id));
    return match ? `${match.team_a} vs ${match.team_b}` : `Match ${match_id}`;
  }, [matches]);

  const filteredData = useMemo(() => {
    return data.filter((r) => {
      let lMatch = true;
      if (leagueFilter !== "all" && leagueFilter !== "") {
         const assocMatch = matches.find((m) => String(m.id) === String(r.match_id) && String(m.league_id) === String(leagueFilter));
         if (!assocMatch) lMatch = false;
      }
      
      const mMatch = matchFilter === "all" || matchFilter === "" ? true : String(r.match_id) === String(matchFilter);
      const sMatch = statusFilter === "all" || statusFilter === "" ? true : r.game_info?.result_status === statusFilter;
      
      return lMatch && mMatch && sMatch;
    });
  }, [data, leagueFilter, matchFilter, statusFilter, matches]);

  const handleView = async (game_id) => {
    try {
      const res = await getGameResultDetails(game_id);
      if (res?.status) {
        setViewData(res);
        setShowViewModal(true);
      }
    } catch {
      toast.error("Failed to load details", toastCfg);
    }
  };

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => <span style={{ color: "#64748b" }}>{row.index + 1 + pagination.pageIndex * pagination.pageSize}</span>,
    },
    {
      accessorKey: "game_id",
      header: "Game ID",
      size: 100,
      cell: ({ getValue }) => <div style={{ fontSize: "0.75rem", fontFamily: "monospace", color: "#64748b" }}>#{getValue()}</div>,
    },
    {
      id: "title",
      header: "Title",
      accessorFn: row => row.game_info?.title,
      cell: ({ getValue }) => <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>{getValue() || "—"}</div>,
    },
    {
      id: "match",
      header: "Match",
      accessorFn: row => row.match_id,
      cell: ({ getValue }) => <span style={{ color: "#475569", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{getMatchName(getValue())}</span>,
    },
    {
      id: "winner",
      header: "Winner",
      enableSorting: false,
      cell: ({ row }) => {
        const wn = row.original?.winners_list?.length > 0 ? row.original.winners_list[0].name : "—";
        return (
          <span style={{ fontWeight: 700, color: wn !== "—" ? "#3b82f6" : "#94a3b8" }}>{wn}</span>
        );
      }
    },
    {
      id: "total_collection",
      header: "Total Pool",
      accessorFn: row => row.pool_stats?.total_collection,
      cell: ({ getValue }) => <span style={{ fontWeight: 800, color: "#16632b" }}>₹{getValue() || 0}</span>,
    },
    {
      id: "status",
      header: "Status",
      accessorFn: row => row.display_status,
      cell: ({ row, getValue }) => {
        const val = getValue();
        const color = row.original.color || "#1d5b29";
        return (
          <span style={{ background: `${color}1A`, color: color, padding: "3px 9px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", border: `1px solid ${color}33`, whiteSpace: "nowrap" }}>
            {val || "—"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Action",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <button style={ts.viewBtn} onClick={() => handleView(row.original.game_id)}>
          <Icon icon="solar:eye-bold" style={{ fontSize: 13 }} /> View Result
        </button>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize, getMatchName]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      const title = row.original.game_info?.title?.toLowerCase() || "";
      const gid = String(row.original.game_id) || "";
      return title.includes(v) || gid.includes(v);
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const rawCount = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h2 style={s.pageTitle}>Selection Game Results</h2>
          <p style={s.pageSub}>
             Track game outcomes, total pools, and administer declarations
          </p>
        </div>
        <button style={s.addBtn} onClick={() => navigate("/add-gameresult")}>
          <Icon icon="solar:check-circle-bold" style={{ fontSize: 18 }} />
          Add Result
        </button>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search by title or ID…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={leagueFilter} onChange={(e) => { setLeagueFilter(e.target.value); setMatchFilter("all"); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Leagues</option>
              {leagues.map((l) => <option key={l.id} value={l.id}>{l.league_name}</option>)}
            </select>

            <select style={s.select} value={matchFilter} onChange={(e) => { setMatchFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Matches</option>
              {matches.filter((m) => leagueFilter === "all" || leagueFilter === "" || String(m.league_id) === String(leagueFilter)).map((m) => (
                <option key={m.id} value={m.id}>{m.team_a} vs {m.team_b}</option>
              ))}
            </select>

            <select style={s.select} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading game results…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:folder-error-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No game results found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{rawCount > 0 ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, rawCount)}</strong>
            {" "}of <strong>{rawCount}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const MathMax = Math.max;
              const MathMin = Math.min;
              const pg = MathMax(0, MathMin(pageIndex - 3, pageCount - 7)) + i;
              if (pg >= pageCount) return null;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <ViewGameModal 
         show={showViewModal}
         onHide={() => setShowViewModal(false)}
         resultData={viewData}
         matches={matches}
      />
    </div>
  );
};